import { Box, Flex, Text, useToast, VStack } from "@chakra-ui/react";
import { UploadSimple } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import FileDragDrop from "components/FileDragDrop";
import { getFolderForRound } from "api/common/fileinfo";
import { apiUpload } from "api/common/fileinfo.service";
import FoundationDataV2Api from "api/issb/foundationDataV2";
import { useAuth } from "context";
import { showErrorToast } from "utils";
import { foundationV2Keys, useRemoveFoundationV2Source } from "hooks/query/issb";
import { normalizeFileInfoList } from "utils/fileinfoIds";
import {
  describeSourceRun,
  extensionOf,
  runForSource,
  SOURCE_EXTENSIONS,
  SOURCE_EXTENSIONS_LABEL,
} from "utils/foundationSourceRuns";
import { groupRowsByRun } from "utils/foundationExtractedRows";
import { buildTargetView } from "utils/foundationTargetRows";
import ExtractionReviewModal from "./ExtractionReviewModal";
import MetricInfoOverlay from "./MetricInfoOverlay";
import SourceRow from "./SourceRow";

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
/* Narrower than v1's, which also accepts .docx/.doc — nothing here can read those. */
const ACCEPT_ATTRIBUTE = SOURCE_EXTENSIONS.map((e) => `.${e}`).join(",");

/**
 * The workstream's source documents, and the extraction of each.
 *
 * Two cards side by side: what has been added, and somewhere to add more. One Review per document, because a
 * run belongs to a file — the server already keys runs that way, and re-extracting one document replaces only
 * that document's own reading of itself.
 */
export default function SourcesPanel({
  dataYearId,
  // The workstream row (entity, year, reporting period), shown in a figure's (i) overlay.
  dataYear = null,
  round,
  canEdit,
  sources,
  persistedSourceIds,
  onSourcesChange,
  runs,
  rows,
  targetStructure,
  // The workstream's current figures, for the review's "current value" comparison.
  currentRows,
  extraction,
  onAccepted,
  onChat,
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { callApi } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);
  const [pendingOpenRunId, setPendingOpenRunId] = useState(null);

  /*
    × removes the document AND its extraction, server-side (ESG-4022). Saving `files` alone could never stick:
    the source list is `files` plus every file with a run, so the run brought the file back on the next load.
    The server also takes it off `files`; the list is updated on screen at once, and the runs and extracted rows
    refetch so its extraction leaves the panel too.
  */
  const removeSource = useRemoveFoundationV2Source({
    onSuccess: () => {
      queryClient.invalidateQueries(foundationV2Keys.runs(dataYearId));
      queryClient.invalidateQueries(foundationV2Keys.rows(dataYearId));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  // Memoised because the auto-open effect depends on it: a fresh Map every render would re-run that effect
  // on every render rather than when the rows actually change.
  const rowsByRun = useMemo(() => groupRowsByRun(rows), [rows]);
  /*
    How many of a run's OWN extracted rows match a datapoint the target structure actually wants, rather
    than the raw count the run finished with — `buildTargetView`'s join is already by name, keyed on the
    structure, so handing it just this run's rows (instead of the whole year's) scopes `.filled` to one
    document for free. No backend involved: `rows` already carries every extracted row for the year, and
    `targetStructure` is already fetched by the page for the table below.

    `undefined` (not 0) when there's no structure at all — `buildTargetView` reports `hasStructure: false`
    for "nothing to match against", which is a different fact from "matched nothing" and must not read as
    the latter in the UI (0 matched vs. no matching happening).
  */
  const matchedCountByRun = useMemo(() => {
    const counts = new Map();
    rowsByRun.forEach((runRows, runId) => {
      const view = buildTargetView(targetStructure, runRows);
      counts.set(runId, view.hasStructure ? view.filled : undefined);
    });
    return counts;
  }, [rowsByRun, targetStructure]);
  /*
    The ids the SERVER holds — not the ids on screen. Built from `sources` this was tautological: every row
    rendered comes from that same array, so nothing was ever reported as unsaved and the UNSAVED state could
    not be reached. The window it covers is real but brief: a dropped file is in `sources` immediately and in
    `question.files` only once the save lands.
  */
  const savedIds = useMemo(
    () => new Set((persistedSourceIds || []).map(String)),
    [persistedSourceIds]
  );

  /*
    Opens a review by itself once the run it started has finished AND written rows. Gated on the rows too, not
    just the status: the rows query is invalidated by the close transition, so a status-only gate flashes an
    empty review for a beat.
  */
  useEffect(() => {
    if (pendingOpenRunId == null) return;
    const run = (runs || []).find((r) => r.id === pendingOpenRunId);
    if (!run || run.status !== "DONE") return;
    if (!(rowsByRun.get(run.id)?.length > 0)) return;
    setOpenRunId(run.id);
    setPendingOpenRunId(null);
  }, [pendingOpenRunId, runs, rowsByRun]);

  const handleFiles = useCallback(
    async (files) => {
      if (!files || !files.length) return;
      setUploading(true);
      const accepted = [];
      for (const file of Array.from(files)) {
        // Named, not silently skipped as v1 does — a file that vanishes on drop reads as a broken drop zone.
        if (!SOURCE_EXTENSIONS.includes(extensionOf(file.name))) {
          showErrorToast(toast, `${file.name} is not a ${SOURCE_EXTENSIONS_LABEL} file.`);
          continue;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          showErrorToast(toast, `${file.name} is larger than ${MAX_FILE_SIZE_MB}MB.`);
          continue;
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tags", ["ISSB"]);
        if (round?.id) formData.append("folders", [getFolderForRound(round.id)]);
        try {
          accepted.push(await apiUpload({ data: formData }));
        } catch (err) {
          showErrorToast(toast, `Could not upload ${file.name}: ${err.message}`);
        }
      }
      setUploading(false);
      if (!accepted.length) return;
      queryClient.invalidateQueries("fileinfoService.list");
      onSourcesChange(normalizeFileInfoList([...(sources || []), ...accepted]));
    },
    [round, sources, onSourcesChange, queryClient, toast]
  );

  const openRun = (runs || []).find((r) => r.id === openRunId) || null;

  return (
    <>
      <Flex gap={4} align="stretch" wrap={{ base: "wrap", md: "nowrap" }}>
        {sources?.length > 0 ? (
          /* Half the row whether or not the upload box sits beside it — when the workstream is no longer
             editable (approved, or a viewer) the upload box is gone and the list would otherwise stretch the
             full width. Grows to the full row only on a narrow screen, where the two stack anyway. */
          <Box
            flex={{ base: "1 1 100%", md: "0 0 calc(50% - 8px)" }}
            minW={0}
            bg="darkTeal"
            border="1px solid"
            borderColor="darkTeal"
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="sm" fontWeight="600" color="white" mb={3}>
              Sources
            </Text>
            <Box borderTop="1px solid" borderColor="whiteAlpha.300">
              {sources.map((fileInfo) => {
                const run = runForSource(runs, fileInfo.id);
                const descriptor = describeSourceRun(fileInfo, run, {
                  isUnsaved: !savedIds.has(String(fileInfo.id)),
                });
                return (
                  <SourceRow
                    key={fileInfo.id}
                    fileInfo={fileInfo}
                    descriptor={descriptor}
                    matchedCount={run ? matchedCountByRun.get(run.id) : undefined}
                    canEdit={canEdit}
                    taskId={extraction.taskIdFor(fileInfo.id, run)}
                    isStarting={extraction.startingFileId === String(fileInfo.id)}
                    /* One extraction at a time: several at once is an untested path through the worker. */
                    isBlocked={extraction.isRunning}
                    onExtract={async () => {
                      const runId = await extraction.startExtraction(fileInfo);
                      if (runId) setPendingOpenRunId(runId);
                    }}
                    onReview={() => setOpenRunId(run.id)}
                    onCancel={() => extraction.cancel(run)}
                    onRemove={() => {
                      const remaining = sources.filter((f) => String(f.id) !== String(fileInfo.id));
                      // A document never extracted has nothing server-side to remove but its place in `files`.
                      if (!run) {
                        onSourcesChange(remaining);
                        return;
                      }
                      removeSource.mutate(
                        { dataYearId, fileInfoId: fileInfo.id },
                        { onSuccess: () => onSourcesChange(remaining) }
                      );
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ) : null}

        {canEdit ? (
          <Box flex={{ base: "1 1 100%", md: "0 0 calc(50% - 8px)" }} minW={0}>
            <FileDragDrop onFiles={handleFiles} borderRadius="lg">
              <VStack
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                py={10}
                px={6}
                spacing={3}
                cursor="pointer"
                h="100%"
                justifyContent="center"
                _hover={{ borderColor: "gray.400" }}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadSimple size={32} color="#85C22A" />
                <Text fontWeight="semibold" textAlign="center">
                  {uploading ? "Uploading..." : "Drop files here or click to browse"}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {SOURCE_EXTENSIONS_LABEL} up to {MAX_FILE_SIZE_MB}MB
                </Text>
              </VStack>
            </FileDragDrop>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              accept={ACCEPT_ATTRIBUTE}
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = null;
              }}
            />
          </Box>
        ) : null}
      </Flex>

      {openRun ? (
        <SourceReview
          run={openRun}
          rows={rowsByRun.get(openRun.id) ?? []}
          canEdit={canEdit}
          dataYearId={dataYearId}
          dataYear={dataYear}
          targetStructure={targetStructure}
          currentRows={currentRows}
          callApi={callApi}
          onClose={() => setOpenRunId(null)}
          onAccepted={onAccepted}
          onChat={onChat}
        />
      ) : null}
    </>
  );
}

/**
 * The review of one extraction, and accepting it.
 *
 * One mounted modal for whichever run is open, never one per row: the dry run fires when the modal opens, so N
 * mounted modals would mean N preview requests on every render of the card.
 */
function SourceReview({
  run,
  rows,
  canEdit,
  dataYear,
  targetStructure,
  currentRows,
  callApi,
  onClose,
  onAccepted,
  onChat,
}) {
  const toast = useToast();
  // The row whose (i) details are open. Chakra stacks modals, so the overlay opens over the review.
  const [detailRow, setDetailRow] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setPreviewing] = useState(false);
  const [isAccepting, setAccepting] = useState(false);
  /*
    Defaults to keeping figures that match no target datapoint, because dropping is the irreversible half of
    the choice and has to be asked for rather than arrived at.
  */
  const [keepOffSchema, setKeepOffSchema] = useState(true);
  /*
    Extracted rows the reviewer crossed out for this ingest. Only rows with a current figure offer the cross,
    so ignoring always means "keep what is there now".

    TODO(backend — not yet built): this is UI ONLY. `POST /runs/{runId}/accept` takes no per-row exclusion,
    so these ids are NOT sent and a crossed-out row is still written on Accept. The dev team needs to add an
    optional `ignoredRowIds` list to acceptRun (controller + FoundationDataV2Facade.acceptRun, filtering the
    extracted rows before the request is built), honoured by the dry run too so the preview counts and the
    expectedReplacedRows guard match. Then pass `ignoredRowIds: ignoredIds` to both acceptRun calls below and
    re-preview on toggle. Until then the modal labels the cross as not yet active.
  */
  const [ignoredIds, setIgnoredIds] = useState([]);

  const runPreview = useCallback(
    async (choices) => {
      setPreviewing(true);
      try {
        setPreview(
          await callApi(FoundationDataV2Api.acceptRun, {
            runId: run.id,
            dryRun: true,
            keepOffSchema: choices.keepOffSchema,
          })
        );
      } catch (err) {
        // A failed preview must not block reading the extraction, which is most of this modal's value. The
        // footer simply says nothing about the effect, and Accept surfaces the real error if pressed.
        setPreview(null);
        console.warn("Could not preview accepting this extraction", err);
      } finally {
        setPreviewing(false);
      }
    },
    [callApi, run.id]
  );

  useEffect(() => {
    if (canEdit) runPreview({ keepOffSchema });
    // Once, on open: re-previewing on every keepOffSchema change is handled explicitly below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Changing the choice RE-PREVIEWS rather than adjusting the numbers locally. Dropping rows changes how many
    are written and can change how many existing ones are superseded, and `expectedReplacedRows` is compared
    against exactly that count — a locally adjusted preview would send a stale number and earn a 409 on a
    workstream nothing else had touched.
  */
  const chooseOffSchema = async (keep) => {
    setKeepOffSchema(keep);
    if (canEdit) await runPreview({ keepOffSchema: keep });
  };

  // UI only for now (see the TODO above): marks the row on screen, does not change what Accept writes.
  const toggleIgnored = (rowId) => {
    setIgnoredIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const doAccept = async () => {
    setAccepting(true);
    try {
      const result = await callApi(FoundationDataV2Api.acceptRun, {
        runId: run.id,
        dryRun: false,
        expectedReplacedRows: preview?.rowsSuperseded,
        keepOffSchema,
      });
      onClose();
      toast({
        title: `Added ${result.rowsWritten} metric${result.rowsWritten === 1 ? "" : "s"} to Foundation Data`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onAccepted?.();
    } catch (err) {
      const stale = err.response?.status === 409;
      // A 409 is either a stale preview or, since ESG-4012, a workstream past the editing stages — the server's
      // message says which, so it wins over the generic stale-preview wording.
      const serverMessage = err.response?.data?.parameters?.message;
      showErrorToast(
        toast,
        stale
          ? serverMessage ?? "Foundation Data changed while this was open. Close and reopen to see the new numbers."
          : serverMessage ?? err.response?.data?.message ?? err.message
      );
      if (stale) onClose();
    } finally {
      setAccepting(false);
    }
  };

  /*
    No "Manage changed data" pre-flight here any more (ESG-4012). Accepting writes to the working set only;
    answers and AI Review do not see it until the owner approves the workstream, which is where the question
    about affected answers is asked now.
  */
  const accept = doAccept;

  /*
    A sibling of the review modal rather than nested in it: Chakra stacks modals, so the extraction stays
    open behind this one and Cancel returns the user to what they were reading.
  */
  return (
    <>
      <ExtractionReviewModal
        isOpen
        onClose={onClose}
        run={run}
        rows={rows}
        canEdit={canEdit}
        preview={preview}
        isPreviewing={isPreviewing}
        isAccepting={isAccepting}
        keepOffSchema={keepOffSchema}
        onKeepOffSchemaChange={chooseOffSchema}
        structure={targetStructure}
        currentRows={currentRows}
        ignoredIds={ignoredIds}
        onToggleIgnored={canEdit ? toggleIgnored : undefined}
        onShowRow={setDetailRow}
        onAccept={accept}
        onChat={onChat}
      />
      <MetricInfoOverlay
        isOpen={!!detailRow}
        onClose={() => setDetailRow(null)}
        row={detailRow}
        dataYear={dataYear}
      />
    </>
  );
}
